import { useCallback } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { blobToBase64 } from 'utils/general';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useMutation, useQueryClient } from 'react-query';
import { apiUpdateUserProfile } from 'api/users/updateUser';
import { useCurrentUserId, useHasUserLevelScopes, useIsLoggedInUser } from 'modules/Auth/hooks';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { either, errorToReactLeft, noop, pipe } from 'utils/fp';
import { fetchUserProfileTE } from 'api/users';
import { constructCoverImageURL, SupportedCoverImageEntities } from 'utils/coverImage';
import { pick } from 'rambda';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';

interface UseAvatarUploadProps {
  onImageSelected: (base64Image: string) => void;
  errorMessage?: string;
}

interface UseAvatarUploadReturn {
  shouldDelete: boolean;
  userId?: string;
  onSuccess?: () => void;
}

export const useUserAvatar = (userId?: string) => {
  const currentUserId = useCurrentUserId();
  const fetchUserId = userId || currentUserId;

  const { data: userAvatarE } = useQueryTE([queryKeys.Users, { userId: fetchUserId }], fetchUserProfileTE({ userId: fetchUserId }, errorToReactLeft));
  const userAvatar = pipe(
    userAvatarE,
    either.fold(
      () => null,
      (data) => pick(['image'], data?.user || {}),
    ),
  );

  return userAvatar;
};

export const useUserAvatarURL = (userId?: string) => {
  const userAvatar = useUserAvatar(userId);
  return userAvatar && constructCoverImageURL(userAvatar as SupportedCoverImageEntities);
};

export const useAvatarUpload = ({ onImageSelected, errorMessage = 'Failed to load image' }: UseAvatarUploadProps) => {
  const handleFileDrop: DropzoneOptions['onDrop'] = useCallback(
    (files) => {
      async function processFile(file: File): Promise<void> {
        try {
          const base64Image = await blobToBase64(file);
          onImageSelected(base64Image);
        } catch (e: unknown) {
          console.error(e);
          cogoToast.error(errorMessage);
        }
      }

      const fileToUpload = files[0];
      if (fileToUpload) {
        processFile(fileToUpload);
      }
    },
    [onImageSelected, errorMessage],
  );

  const dropzoneConfig: DropzoneOptions = {
    onDrop: handleFileDrop,
    maxFiles: 1,
    noClick: true,
    multiple: false,
    maxSize: 20 * 1024 * 1024, // 20MB
    accept: 'image/jpg,image/jpeg,image/png',
  };

  const { open: openFileDialog, getInputProps, getRootProps } = useDropzone(dropzoneConfig);

  return {
    openFileDialog,
    getInputProps,
    getRootProps,
  };
};

export const useCanUpdateAvatar = (userId?: string) => {
  const hasAccess = useHasUserLevelScopes([permissionMatchers.user.update], userId);
  return hasAccess;
};

export const useUpdateAvatar = ({ userId, shouldDelete, onSuccess: onSuccessCallback }: UseAvatarUploadReturn) => {
  const queryClient = useQueryClient();
  const currentUserId = useCurrentUserId();
  const isLoggedInUser = useIsLoggedInUser();
  const updateForUserId = userId || currentUserId;
  const canUpdateAvatar = useCanUpdateAvatar(userId);

  const avatarQueryKey = [queryKeys.Users, { userId: updateForUserId }];

  const { mutate: updateAvatar, isLoading } = useMutation(
    (image?: CF.API.Image) =>
      apiUpdateUserProfile({
        userId: updateForUserId,
        body: {
          info: { image },
          action: { op: shouldDelete ? 'delete' : 'overwrite' },
        },
      }),
    {
      onSuccess: () => {
        if (isLoggedInUser) {
          queryClient.invalidateQueries([queryKeys.LoggedInUser]);
        }

        queryClient.invalidateQueries(avatarQueryKey);
        onSuccessCallback?.();
      },
      onError: (error: CF.API.ApiError) => {
        if (error.status) {
          cogoToast.error(error.status.details, { heading: error.status.description });
          return;
        }

        cogoToast.error(error.message, { heading: 'Failed to update avatar' });
      },
    },
  );

  return { updateAvatar: canUpdateAvatar ? updateAvatar : noop, isLoading, canUpdateAvatar };
};
